import * as algolia from '@boutique/algolia'
import * as entities from '@boutique/entities'
import * as cold from '@owl-nest/cold-data/client'
import * as utils from '@boutique/utils'
import { type SearchResponse } from '@algolia/client-search'

const CRITERIA = [
  ...cold.taxonomies['pilier-environnemental'].children.tags.map((slug) => ({
    icon: utils.symbols.getCriterionSymbolName(cold.tags[slug].pillar?.icon as string),
    name: cold.tags[slug].name,
    slug: slug,
  })),
  ...cold.taxonomies['pilier-social'].children.tags.map((slug) => ({
    icon: utils.symbols.getCriterionSymbolName(cold.tags[slug].pillar?.icon as string),
    name: cold.tags[slug].name,
    slug: slug,
  })),
]

export type ExtraFacet<NAME extends string> = algolia.product.Facet & { name: NAME }

type Faceters<FILTERS extends Record<string, any>> = {
  [K in keyof FILTERS]: (value: FILTERS[K]) => { value: FILTERS[K]; excluded?: boolean }[]
}

// Each "faceter" return the list of values applied to the facet, and validates
// the value (like removing brands that does not exists). Typically a "faceter"
// will only ever return one value. But for special filters (like for git ideas)
// one filter applies multiple values (filter on an union of multiple tags). A
// faceter can `exclude` the facet, to apply the filter, but not actually do
// facetting (like for category or hierarchy)
export function facets<FILTERS extends Record<string, any>>(
  facteFilters: Partial<FILTERS> | undefined,
  filters: readonly { name: keyof FILTERS & string; attribute: algolia.product.Facet['attribute'] }[],
  faceters: Faceters<FILTERS>,
): ExtraFacet<keyof FILTERS & string>[] {
  const facets: ExtraFacet<keyof FILTERS & string>[] = []

  if (facteFilters === undefined) {
    return facets
  }

  for (const { name, attribute } of filters) {
    const value = facteFilters[name]
    const faceter = faceters[name]
    if (value !== undefined && value !== null) {
      faceter(value).forEach(({ value, excluded }) => {
        facets.push({ name, attribute, value, excluded })
      })
    }
  }

  return facets
}

export function toSearchResult<FILTERS extends Record<string, any>>(
  response: algolia.product.Response,
  facets: ExtraFacet<keyof FILTERS & string>[],
  filters: readonly { name: keyof FILTERS; attribute: string }[],
): entities.search.Result<FILTERS> {
  const facetResults = buildResultFacets(response, facets, filters)

  const firstResult = response.results[0] as SearchResponse<algolia.product.Product>

  return {
    nbHits: firstResult.nbHits,
    nbPages: firstResult.nbPages,
    // this complex nested map, entries and fromEntries is only here to slugify
    // every key in each facet
    facets: Object.fromEntries(
      Object.entries(facetResults).map(([key, value]) => {
        return [
          key,
          value === undefined
            ? undefined
            : {
                ...value,
                data: Object.fromEntries(
                  Object.entries(value.data).map(([key, value]) => {
                    return [utils.text.slugify(key, '-'), value]
                  }),
                ),
              },
        ]
      }),
    ) as typeof facetResults,
    hits: firstResult.hits.map((hit) => {
      return entities.product.hitProduct(hit, {
        brands: Object.values(cold.brands),
        criteria: CRITERIA,
      })
    }),
  }
}

function buildResultFacets<FILTERS extends Record<string, any>>(
  response: algolia.product.Response,
  facets: ExtraFacet<keyof FILTERS & string>[],
  filters: readonly { name: keyof FILTERS; attribute: string }[],
): entities.search.Result<FILTERS>['facets'] {
  const resultFacets: entities.search.Result<FILTERS>['facets'] = {}

  const firstResult = response.results[0] as SearchResponse<algolia.product.Product>

  // first fill facets with values from default search (if facet was not a filter)
  for (const filter of filters) {
    const resultFacet = firstResult.facets?.[filter.attribute]
    resultFacets[filter.name] = (
      resultFacet
        ? { data: resultFacet, total: Object.values(resultFacet).reduce((sum, count) => sum + count, 0) }
        : undefined
    ) as any
  }

  // then for each facets that was a filter, fill facets with values from facet search
  const facetLists = filters
    .map((filter) => facets.find((facet) => facet.name === filter.name))
    .filter((facet): facet is ExtraFacet<keyof FILTERS & string> => facet !== undefined && !facet.excluded)

  for (const [index, facet] of facetLists.entries()) {
    const result = response.results[index + 1] as SearchResponse<algolia.product.Product>
    const resultFacet = result.facets?.[facet.attribute] ?? firstResult.facets?.[facet.attribute]
    resultFacets[facet.name] = (
      resultFacet
        ? { data: resultFacet, total: Object.values(resultFacet).reduce((sum, count) => sum + count, 0) }
        : undefined
    ) as any
  }

  // then price facet
  const result = response.results[response.results.length - 1] as SearchResponse<algolia.product.Product>
  const priceFacet = result.facets?.['price']
  const priceFacetStats = result.facets_stats?.['price']
  resultFacets.price =
    priceFacet && priceFacetStats
      ? {
          data: priceFacet,
          ...priceFacetStats,
          total: Object.values(priceFacet).reduce((sum, count) => sum + count, 0),
        }
      : undefined

  // filter undefined
  return Object.fromEntries(Object.entries(resultFacets).filter(([key, value]) => value !== undefined)) as any
}
